"use client";

import { useId, useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { mainConfig } from "@/configs/main.config";
import { useBlockchain } from "@/providers/global-data.provider";

// Calculate emission data focusing on the continuously decaying block reward
const calculateEmissionData = (currentBlock: number, currentBlockReward: number) => {
  const yearsData = [];

  // Initial parameters
  const {
    newRewardApplyBlock,
    newStakeRewardApplyBlock,
    reductionInterval,
    reductionFactor,
    weeksPerYear,
    initialReward,
  } = mainConfig.rewardSystem;

  // Add data point for the current reward
  yearsData.push({
    name: 'Current',
    blockReward: currentBlockReward.toFixed(4),
    block: currentBlock.toLocaleString(),
  });

  // Calculate for years 2-16 (15 years)
  for (let year = 1; year <= 15; year++) {
    // Calculate the reward after 'year' years of weekly reductions
    const weeksElapsed = year * weeksPerYear;
    let reward = initialReward;

    // Apply all weekly reductions
    for (let week = 0; week < weeksElapsed; week++) {
      reward *= reductionFactor;
    }

    // Apply staking split if applicable (after block 1,952,106)
    // This is a simplification - in reality, we'd need to check if the block number
    // at this point in time is past the staking apply block
    if (currentBlock >= newStakeRewardApplyBlock || year > 2) {
      reward *= 0.8; // 80% to miners after staking is applied
    }

    // Add data point for this year
    yearsData.push({
      name: `Year ${year + 1}`,
      blockReward: reward.toFixed(4),
      block: (newRewardApplyBlock + (weeksElapsed * reductionInterval)).toLocaleString(),
    });
  }

  return yearsData;
};

export function EmissionChart() {
  const id = useId();
  const { currentBlockReward, currentBlock, isLoading, error } = useBlockchain();
  const [emissionData, setEmissionData] = useState<{
      name: string;
      blockReward: string;
      block: string;
  }[]>([]);

  useEffect(() => {
    if (!isLoading && !error) {
      const data = calculateEmissionData(currentBlock, currentBlockReward);
      setEmissionData(data);
    }
  }, [currentBlock, currentBlockReward, isLoading, error]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#FF8A3C] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading emission data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <p className="text-red-500">Error loading emission data</p>
      </div>
    );
  }

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={emissionData}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id={`${id}-reward-gradient`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FF8A3C" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#FF8A3C" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip
            formatter={(value) => [`${value} LRS`, 'Block Reward']}
            labelFormatter={(label) => {
              const dataPoint = emissionData.find(item => item.name === label);
              return dataPoint ? `${label} (Block: ${dataPoint.block})` : label;
            }}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey="blockReward"
            name="Avg Block Reward"
            stroke="#FF8A3C"
            fillOpacity={1}
            fill={`url(#${id}-reward-gradient)`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
